import React, { useContext } from 'react';
import {
    DocumentContext,
    DocumentExtension,
    DocumentIcon,
    DossierFichier,
    useDocumentPreviewContext,
    useDossierDocuments,
    useDossierDocumentsFiles,
    useDossierFormulaireContext,
} from '@europrocurement/l2d-modules';
import { Box, Button, List, ListItem, Tooltip } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { DisplayedDocument } from '@europrocurement/l2d-modules/modules/Dossiers/providers/types';
import { useSnackbar } from 'notistack';

type DossierValidationBreadcrumbProps = {
    isDefaultLayout: boolean;
    isFullFormLayout: boolean;
};
const DossierValidationBreadcrumb: React.FunctionComponent<DossierValidationBreadcrumbProps> =
    function ({ isDefaultLayout, isFullFormLayout }) {
        const { currentPrestationId, setCurrentPrestationId, formulaires } =
            useDossierFormulaireContext();
        const { getFilePreview } = useDossierDocumentsFiles();
        const { listDocuments, documentPreview, setPreviewLoading, setDocumentPreview } =
            useDocumentPreviewContext();
        const { fichiers } = useContext(DocumentContext);
        const { enqueueSnackbar } = useSnackbar();
        const { downloadFichier, isFileSupported } = useDossierDocuments();
        const { getColorByStatus, getFileIcon } = useDossierDocuments();

        const listSx = {
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '0 20px',
            padding: 0,
            paddingRight: '48px',
            marginTop: '1em',
        };

        const listItemSx = {
            display: 'flex',
            alignItems: 'center',
            width: 'auto',
            gap: '0 5px',
            padding: 0,
        };

        const listItemButtonSx = {
            display: 'flex',
            alignItems: 'center',
            gap: '0 5px',
            fontSize: '13px',
            ':hover': {
                color: 'primary.main',
            },
        };

        const listItemIconSx = {
            display: 'flex',
            alignItems: 'center',
            width: 'auto',
            fontSize: '10px',
            lineHeight: '0',
        };

        const handleDisplayOtherDoc = async (document: DisplayedDocument) => {
            if (document.fichierId) {
                setPreviewLoading(true);
                try {
                    const docPreview = await getFilePreview(document.id, document.fichierId);

                    if (!isFileSupported(docPreview.fileType as DocumentExtension)) {
                        const fichier = fichiers.find(
                            (f: DossierFichier) => Number(f.id) === document.fichierId,
                        );

                        if (fichier) {
                            await downloadFichier(fichier);
                        }
                    }

                    setDocumentPreview(docPreview);
                    setPreviewLoading(false);
                } catch (err) {
                    console.log(err);
                    enqueueSnackbar(
                        `Une erreur est survenue lors de l'affichage du document ${document.label}`,
                        { variant: 'error' },
                    );
                }
            }
        };

        return (
            <Box sx={{ display: 'flex', justifyContent: 'start' }}>
                {!isFullFormLayout && listDocuments.length > 0 && (
                    <List sx={{ ...listSx, width: '70%' }}>
                        {listDocuments
                            .filter((doc) => doc.fichierId)
                            .map((document, i) => {
                                const isCurrent = document.label === documentPreview?.documentLabel;
                                const color = document.feedback
                                    ? getColorByStatus(
                                          document.status as 'pending' | 'error' | 'success',
                                      )
                                    : 'primary';
                                return (
                                    <ListItem
                                        sx={listItemSx}
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`${document.label}_${i}`}
                                    >
                                        <Tooltip
                                            title={document.label}
                                            arrow
                                            placement="top"
                                        >
                                            <Button
                                                sx={{
                                                    ...listItemButtonSx,
                                                    color: isCurrent
                                                        ? 'primary.main'
                                                        : 'text.gris35',
                                                }}
                                                onClick={() => handleDisplayOtherDoc(document)}
                                            >
                                                <DocumentIcon
                                                    color={color}
                                                    icon={
                                                        document.type
                                                            ? getFileIcon(document.type)
                                                            : 'file'
                                                    }
                                                    sx={{ width: '5px', height: '5px' }}
                                                />
                                                {document.label.length > 30
                                                    ? `${document.label.substring(0, 27)}[...]`
                                                    : document.label}
                                            </Button>
                                        </Tooltip>
                                    </ListItem>
                                );
                            })}
                    </List>
                )}
                {(isDefaultLayout || isFullFormLayout) && (
                    <List sx={{ ...listSx, width: '30%' }}>
                        {formulaires.map((formulaire) => {
                            const isCurrent = formulaire.prestationId === currentPrestationId;

                            return (
                                <ListItem
                                    sx={listItemSx}
                                    key={formulaire.prestationId}
                                >
                                    <Tooltip
                                        title={formulaire.label}
                                        arrow
                                        placement="top"
                                    >
                                        <Button
                                            sx={{
                                                ...listItemButtonSx,
                                                color: isCurrent ? 'primary.main' : 'text.gris35',
                                            }}
                                            onClick={() =>
                                                setCurrentPrestationId(formulaire.prestationId)
                                            }
                                        >
                                            {formulaire.complete && (
                                                <FaOptionIcon
                                                    data-testid="icon-complete"
                                                    icon={['fasdl', 'check']}
                                                    sx={listItemIconSx}
                                                />
                                            )}
                                            {formulaire.label.length > 30
                                                ? `${formulaire.label.substring(0, 27)}[...]`
                                                : formulaire.label}
                                        </Button>
                                    </Tooltip>
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </Box>
        );
    };

export default DossierValidationBreadcrumb;
